import { afterScrollRestore, hashTarget, motionAllowed, onMotionChange, startsBelowScreen } from '@theme/bdc-motion';

/**
 * Braun Design Co scroll reveals, loaded on every page by layout/theme.liquid.
 *
 * A section opts in with `data-bdc-reveal` on its root and `data-bdc-reveal-item` on its
 * heading group and items (`="media"` on pure images). The rules are in assets/bdc.css,
 * section 12. A root's value moves through:
 * - "" (untouched): every root starts here and stays here if any of it is on or above the
 *   screen when the script starts, so nothing in view ever moves;
 * - "pending": set only on roots that start wholly below the screen;
 * - "shown": the root has scrolled in, and its items move into place;
 * - "instant": focus, a link target, printing, the theme editor or motion turning off
 *   shows the root at once;
 * - "done": the reveal is over, and no reveal rule applies any more.
 *
 * Without this script, or when motion is not allowed at load, nothing is ever hidden.
 * In the theme editor a reloaded or newly added section is left untouched, so it shows
 * at once, and selecting a section or block shows it at once.
 */

const ROOT = '[data-bdc-reveal]';
const ITEM = '[data-bdc-reveal-item]';
const STAGGER = 60;
const STAGGER_CAP = 300;
/** The 350ms reveal plus the longest stagger, with some room */
const SETTLE = 700;

/** Roots the observer has reported at least once */
const seen = new WeakSet();

const observer = new IntersectionObserver(onIntersect, {
  rootMargin: '0px 0px -10% 0px',
  threshold: 0,
});

// Decide what is below the screen only once a reload or Back has restored the scroll
// position, or the sections the restore lands on would play their reveal
afterScrollRestore(() => {
  if (motionAllowed()) start();
});

function start() {
  const hashRoot = closestRoot(hashTarget());

  for (const root of document.querySelectorAll(ROOT)) {
    if (root !== hashRoot) observer.observe(root);
  }

  document.addEventListener('focusin', (event) => showNow(closestRoot(event.target)), true);
  window.addEventListener('hashchange', () => showNow(closestRoot(hashTarget())));
  window.addEventListener('beforeprint', showAll);

  document.addEventListener('shopify:section:select', (event) => showNow(editorRoot(event.target)));
  document.addEventListener('shopify:block:select', (event) => showNow(editorRoot(event.target)));
  document.addEventListener('shopify:section:unload', (event) => {
    for (const root of rootsIn(event.target)) observer.unobserve(root);
  });

  onMotionChange((allowed) => {
    if (!allowed) showAll();
  });
}

/** @param {IntersectionObserverEntry[]} entries */
function onIntersect(entries) {
  for (const entry of entries) {
    const root = /** @type {HTMLElement} */ (entry.target);

    // First report: hide the root only if it starts wholly below the screen
    if (!seen.has(root)) {
      seen.add(root);

      if (!root.dataset.bdcReveal && startsBelowScreen(entry) && !holdsFocusOrTarget(root)) {
        root.dataset.bdcReveal = 'pending';
      } else {
        observer.unobserve(root);
      }
      continue;
    }

    if (entry.isIntersecting && root.dataset.bdcReveal === 'pending') reveal(root);
  }
}

/**
 * Focus or a link target is already inside, for example after a fast Tab before the first
 * report. (matches(':has()') would throw in a browser without :has, so it isn't used.)
 *
 * @param {HTMLElement} root
 */
function holdsFocusOrTarget(root) {
  return root.contains(document.activeElement) || root.matches(':target') || root.querySelector(':target') !== null;
}

/** @param {HTMLElement} root */
function reveal(root) {
  observer.unobserve(root);

  root.querySelectorAll(ITEM).forEach((item, index) => {
    const delay = Math.min(index * STAGGER, STAGGER_CAP);
    /** @type {HTMLElement} */ (item).style.setProperty('--bdc-reveal-delay', `${delay}ms`);
  });

  root.dataset.bdcReveal = 'shown';
  setTimeout(() => finish(root), SETTLE);
}

/** @param {HTMLElement | null} root */
function showNow(root) {
  const state = root?.dataset.bdcReveal;
  if (!root || (state !== 'pending' && state !== 'shown')) return;

  observer.unobserve(root);
  root.dataset.bdcReveal = 'instant';

  // Two frames, so the browser draws "instant" (no transitions) before "done"
  requestAnimationFrame(() => requestAnimationFrame(() => finish(root)));
}

function showAll() {
  for (const root of document.querySelectorAll(ROOT)) showNow(/** @type {HTMLElement} */ (root));
}

/** @param {HTMLElement} root */
function finish(root) {
  if (root.dataset.bdcReveal === 'done') return;

  root.dataset.bdcReveal = 'done';
  root.querySelectorAll(ITEM).forEach((item) => {
    /** @type {HTMLElement} */ (item).style.removeProperty('--bdc-reveal-delay');
  });
}

/**
 * The reveal root an element is inside, if any.
 *
 * @param {EventTarget | Element | null} element
 * @returns {HTMLElement | null}
 */
function closestRoot(element) {
  return element instanceof Element ? element.closest(ROOT) : null;
}

/**
 * The reveal root a theme editor event is about. A section event's target is the
 * section's outer wrapper, so the root is inside it; a block event's is the block.
 *
 * @param {EventTarget | null} element
 * @returns {HTMLElement | null}
 */
function editorRoot(element) {
  return closestRoot(element) ?? (element instanceof Element ? element.querySelector(ROOT) : null);
}

/** @param {EventTarget | null} element */
function rootsIn(element) {
  return element instanceof Element ? element.querySelectorAll(ROOT) : [];
}
